/**
 * 결제 — 학원 모드. 학생별 결제 관리.
 * - 학생 행: 사진 + 이름 + 나이 + 부모 전화번호 + 펼침 화살표
 * - 행 탭 → 아코디언으로 결제 상세 영역 펼침 (상세 내용은 후속 단계에서 채움)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronDown, IoPersonCircleOutline } from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';

const formatKRW = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

const formatMD = (dateStr) => {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${Number(m)}/${Number(d)}`;
};

/** 더미 결제 데이터 — 학생 인덱스로 D-day 다양하게 분포 (데모 UI 점검용) */
const PAY_OFFSETS = [3, 3, 1, 0, 0, -1, -2, 3, 2, -3]; // 학생 i번째의 마감까지 일수
const buildPaymentInfo = (academy, studentIndex = 0) => {
  const tuitionRaw = academy?.tuition?.[0]?.price || '';
  const tuitionNum = Number(String(tuitionRaw).replace(/[^0-9]/g, ''));
  const amount = tuitionNum >= 10000 ? tuitionNum : (tuitionNum * 10000) || 320000;

  const today = new Date();
  const p = (n) => String(n).padStart(2, '0');

  const offset = PAY_OFFSETS[studentIndex % PAY_OFFSETS.length];
  const dueDate = new Date(today);
  dueDate.setDate(today.getDate() + offset);
  const dueStr = `${dueDate.getFullYear()}-${p(dueDate.getMonth() + 1)}-${p(dueDate.getDate())}`;
  const dDay = offset; // 양수=남음 / 0=오늘 / 음수=연체

  // 지난 납부 — 지난 2개월, 매월 5일
  const history = [];
  for (let i = 1; i <= 2; i += 1) {
    const dt = new Date(today.getFullYear(), today.getMonth() - i, 5);
    history.push({
      label: `${dt.getMonth() + 1}월 수강료`,
      amount,
      paidAt: `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`,
    });
  }
  return {
    next: { label: `${dueDate.getMonth() + 1}월 수강료`, amount, dueDate: dueStr, dDay },
    history,
  };
};

const dDayLabel = (d) => {
  if (d > 0) return `D-${d}`;
  if (d === 0) return '오늘 마감';
  return `${-d}일 연체`;
};

const BizPaymentPage = () => {
  const navigate = useNavigate();
  const { operatedAcademy } = useAcademy();
  const aid = operatedAcademy?.id;

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!aid) { setLoading(false); return; }
    setStudents(await academyService.getStudents(aid));
    setLoading(false);
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  if (!operatedAcademy) {
    return (
      <Container>
        <Empty>
          <EmptyText>운영 중인 학원이 없어요.</EmptyText>
          <EmptyBtn type="button" onClick={() => navigate('/academy/register')}>학원 신청하기</EmptyBtn>
        </Empty>
      </Container>
    );
  }
  if (loading) return <BrandLoader />;

  return (
    <Container>
      <Title>결제</Title>
      <Sub>{operatedAcademy.name} · 학생 {students.length}명</Sub>

      {students.length === 0 ? (
        <Hint>아직 등록된 학생이 없어요.</Hint>
      ) : (
        <List>
          {students.map((s, idx) => {
            const open = openId === s.id;
            const info = buildPaymentInfo(operatedAcademy, idx);
            const urgent = info.next.dDay <= 0;
            return (
              <RowWrap key={s.id} $urgent={urgent}>
                <Row type="button" onClick={() => setOpenId(open ? null : s.id)} $open={open}>
                  <Avatar $photo={s.photoUrl}>
                    {!s.photoUrl && <IoPersonCircleOutline size={42} color="#9CA3AF" />}
                  </Avatar>
                  <Main>
                    <TopLine>
                      <Name>{s.name}</Name>
                      {s.age != null && s.age !== '' && <Age>{s.age}세</Age>}
                      {urgent && (
                        <PayBadge $kind={info.next.dDay === 0 ? 'due' : 'overdue'}>
                          {dDayLabel(info.next.dDay)}
                        </PayBadge>
                      )}
                    </TopLine>
                    {s.phone && <Phone>부모님 {s.phone}</Phone>}
                  </Main>
                  <ChevWrap $open={open}><IoChevronDown size={20} /></ChevWrap>
                </Row>

                {open && (() => {
                  const sendLink = (e) => {
                    e.stopPropagation();
                    // TODO: 이노페이 연동 — 학부모님께 결제 링크 발송
                    alert(`${s.name} 학부모님께 결제 링크를 보냈어요. (이노페이 연동 예정)`);
                  };
                  return (
                    <Panel>
                      <PanelSec>
                        <PanelLabel>다음 결제</PanelLabel>
                        <NextCard $urgent={urgent}>
                          <NextTop>
                            <NextLabel>{info.next.label}</NextLabel>
                            <NextAmt>{formatKRW(info.next.amount)}</NextAmt>
                          </NextTop>
                          <NextBottom>
                            <NextDue>마감 {formatMD(info.next.dueDate)}</NextDue>
                            <DDay $urgent={urgent}>{dDayLabel(info.next.dDay)}</DDay>
                          </NextBottom>
                          {urgent && (
                            <SendLinkBtn type="button" onClick={sendLink}>
                              학부모님께 결제 링크 보내기
                            </SendLinkBtn>
                          )}
                        </NextCard>
                      </PanelSec>

                      <PanelSec>
                        <PanelLabel>지난 납부 내역</PanelLabel>
                        {info.history.map((h, i) => (
                          <HistRow key={i}>
                            <HistMain>
                              <HistLabel>{h.label}</HistLabel>
                              <HistDate>납부 {formatMD(h.paidAt)}</HistDate>
                            </HistMain>
                            <HistAmt>{formatKRW(h.amount)}</HistAmt>
                          </HistRow>
                        ))}
                      </PanelSec>
                    </Panel>
                  );
                })()}
              </RowWrap>
            );
          })}
        </List>
      )}
    </Container>
  );
};

export default BizPaymentPage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 22px 16px;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
  margin-bottom: 16px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowWrap = styled.div`
  border-radius: 13px;
  background: #fff;
  border: 1.5px solid ${({ $urgent, theme }) => ($urgent ? theme.colors.primary : theme.colors.border)};
  overflow: hidden;
`;

const Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 12px;
  text-align: left;
  background: ${({ $open, theme }) => ($open ? theme.colors.primaryLight : '#fff')};
  &:active { opacity: 0.85; }
`;

const Avatar = styled.div`
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $photo }) => ($photo ? `#fff url(${$photo}) center / cover no-repeat` : '#F0F1F4')};
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TopLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const Name = styled.span`
  font-size: 14.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Age = styled.span`
  flex-shrink: 0;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const PayBadge = styled.span`
  flex-shrink: 0;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
`;

const Phone = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChevWrap = styled.span`
  flex-shrink: 0;
  display: flex;
  color: ${({ theme }) => theme.colors.text.hint};
  transition: transform 0.2s;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Panel = styled.div`
  padding: 14px 14px 16px;
  background: #FAFBFC;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const PanelSec = styled.section`
  & + & { margin-top: 14px; }
`;

const PanelLabel = styled.p`
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 7px;
  letter-spacing: 0.2px;
`;

/* 다음 결제 카드 */
const NextCard = styled.div`
  padding: 12px 13px;
  border-radius: 11px;
  background: ${({ $urgent, theme }) => ($urgent ? theme.colors.primaryLight : '#fff')};
  border: 1px solid ${({ $urgent, theme }) => ($urgent ? theme.colors.primary : '#E5E7EB')};
`;

const NextTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const NextLabel = styled.span`
  font-size: 14.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NextAmt = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const NextBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 4px;
`;

const NextDue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DDay = styled.span`
  padding: 3px 9px;
  border-radius: 7px;
  font-size: 11.5px;
  font-weight: 800;
  color: ${({ $urgent, theme }) => ($urgent ? '#fff' : theme.colors.primary)};
  background: ${({ $urgent, theme }) => ($urgent ? theme.colors.primary : theme.colors.primaryLight)};
`;

const SendLinkBtn = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 14px;
  font-weight: 800;
`;

/* 지난 납부 내역 */
const HistRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 9px;
  background: #fff;
  border: 1px solid #EEF0F4;
  & + & { margin-top: 6px; }
`;

const HistMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const HistLabel = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HistDate = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const HistAmt = styled.span`
  flex-shrink: 0;
  font-size: 13.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Hint = styled.p`
  text-align: center;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 40px 0;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 100px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBtn = styled.button`
  padding: 12px 26px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
`;
